import { useState } from "react";
import { Link } from "react-router-dom";
import { CaretLeft, CaretRight, Truck, Package } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, addDays, todayStr, qty } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";

const DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const weekStart = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return addDays(iso, -((d.getDay() + 6) % 7));
};
const dayLabel = (iso) => `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]}`;

export default function ShipmentCalendar() {
  const [start, setStart] = useState(weekStart(todayStr()));
  const end = addDays(start, 6);
  const [data] = useLoad(() => api.get("/shipments/calendar", { params: { start, end } }).then((r) => r.data), [start, end]);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const t = todayStr();
  const planned = data?.planned || [];
  const done = data?.done || [];

  return (
    <section className="panel overflow-hidden" data-testid="shipment-calendar">
      <header className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <Button size="icon" variant="ghost" onClick={() => setStart(addDays(start, -7))} data-testid="calendar-prev-button"><CaretLeft /></Button>
        <span className="font-display text-base font-bold" data-testid="calendar-range">{dayLabel(start)} – {dayLabel(end)} {end.slice(0, 4)}</span>
        <Button size="icon" variant="ghost" onClick={() => setStart(addDays(start, 7))} data-testid="calendar-next-button"><CaretRight /></Button>
        <Button size="sm" variant="outline" className="rounded-[4px]" onClick={() => setStart(weekStart(t))} data-testid="calendar-today-button">Bu Hafta</Button>
        <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 bg-orange-400" /> Planlanan (sipariş teslim tarihi)</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 bg-teal-600" /> Yapılan sevk / mal kabul</span>
        </div>
      </header>
      <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-7 sm:divide-x sm:divide-y-0">
        {days.map((d, i) => {
          const p = planned.filter((x) => x.date === d);
          const s = done.filter((x) => x.date === d);
          return (
            <div key={d} className={`min-h-[180px] ${d === t ? "bg-sky-50" : ""}`} data-testid={`calendar-day-${d}`}>
              <div className={`flex items-baseline justify-between px-3 py-2 text-xs font-bold uppercase tracking-wide ${d === t ? "text-sky-700" : "text-muted-foreground"}`}>
                <span>{DAYS[i]}</span><span className="font-num">{dayLabel(d)}</span>
              </div>
              <div className="space-y-1.5 px-2 pb-3">
                {p.map((o) => (
                  <Link key={o.id} to={`/siparisler/${o.id}`} data-testid={`calendar-planned-${o.no}`}
                    className={`block border-l-4 px-2 py-1.5 text-xs transition-colors hover:bg-orange-100 ${o.overdue ? "border-red-500 bg-red-50" : "border-orange-400 bg-orange-50"}`}>
                    <div className="flex items-center gap-1 font-semibold"><Truck size={12} /> {o.no}{o.overdue && <span className="ml-auto text-[10px] text-red-600">gecikti</span>}</div>
                    <div className="truncate text-muted-foreground">{o.account_name}</div>
                    <div className="font-num text-[11px]">{qty(o.remaining_qty)} kalan · {o.kind === "sale" ? "Sevk" : "Mal Kabul"}</div>
                  </Link>
                ))}
                {s.map((x) => (
                  <Link key={x.id} to={`/irsaliye/${x.id}`} target="_blank" data-testid={`calendar-done-${x.no}`}
                    className="block border-l-4 border-teal-600 bg-teal-50 px-2 py-1.5 text-xs transition-colors hover:bg-teal-100">
                    <div className="flex items-center gap-1 font-semibold"><Package size={12} /> {x.no}</div>
                    <div className="truncate text-muted-foreground">{x.account_name}</div>
                    <div className="font-num text-[11px]">{qty(x.qty)} adet · {x.order_no}</div>
                  </Link>
                ))}
                {p.length + s.length === 0 && <div className="px-2 pt-4 text-center text-[11px] text-muted-foreground/60">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
